import api from './api'

export type ClosingChecklistItem = {
  id: string
  label: string
  status: string
  responsible: string | null
  dueDate: string | null
  notes: string | null
}

export type ClosingChecklistCategory = {
  name: string
  items: ClosingChecklistItem[]
}

export type ClosingChecklist = {
  id: string
  loanId: string
  status: string
  completionPct: number
  categories: ClosingChecklistCategory[]
}

type RawChecklistItem = {
  id?: unknown
  label?: unknown
  status?: unknown
  responsible?: unknown
  due_date?: unknown
  dueDate?: unknown
  notes?: unknown
}

type RawChecklistCategory = {
  category_name?: unknown
  categoryName?: unknown
  items?: unknown
}

type RawChecklist = {
  id?: unknown
  loan_id?: unknown
  loanId?: unknown
  status?: unknown
  completion_pct?: unknown
  completionPct?: unknown
  categories?: unknown
}

function text(value: unknown, fallback = ''): string {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback
}

export function mapClosingChecklist(raw: RawChecklist): ClosingChecklist {
  const categories = Array.isArray(raw.categories) ? raw.categories : []
  return {
    id: text(raw.id),
    loanId: text(raw.loan_id ?? raw.loanId),
    status: text(raw.status, 'Active'),
    completionPct: Number(raw.completion_pct ?? raw.completionPct ?? 0) || 0,
    categories: categories.map((categoryValue) => {
      const category = (categoryValue ?? {}) as RawChecklistCategory
      const items = Array.isArray(category.items) ? category.items : []
      return {
        name: text(category.category_name ?? category.categoryName, 'Checklist'),
        items: items.map((itemValue) => {
          const item = (itemValue ?? {}) as RawChecklistItem
          return {
            id: text(item.id),
            label: text(item.label, 'Untitled checklist item'),
            status: text(item.status, 'Pending'),
            responsible: text(item.responsible) || null,
            dueDate: text(item.due_date ?? item.dueDate) || null,
            notes: text(item.notes) || null,
          }
        }),
      }
    }),
  }
}

export const journalService = {
  listChecklists: (loanId: string): Promise<ClosingChecklist[]> =>
    api
      .get('/checklists/', { params: { loan_id: loanId } })
      .then((response) => (Array.isArray(response.data) ? response.data : []).map(mapClosingChecklist)),

  updateChecklistItem: (
    checklistId: string,
    itemId: string,
    newStatus: string,
    notes?: string,
  ): Promise<ClosingChecklist> =>
    api
      .post(`/checklists/${checklistId}/items/status`, {
        item_id: itemId,
        new_status: newStatus,
        notes: notes || undefined,
      })
      .then((response) => mapClosingChecklist(response.data)),
}
